// **The rounds phase channel** (`rounds-client` **D1**).
//
// One polled read behind one cached snapshot. Built in the exact shape of the shipped
// `AdvisoryPromptCooldown` seam (`ui/colony/prompt-cooldown.ts`) and of
// `createRoundsPromptChannel` beside it: `snapshot()` returns a value **cached between
// notifications**, `subscribe(onChange)` returns an unsubscribe, and the countdown runs on an
// **injected** `Clock` + `Scheduler` so a five-minute editing phase costs a test no seconds.
//
// **Why one channel carries so much.** `client-app`'s delta names "the phase channel" and "the
// standings/submission read" as two things. In shipped terms the submission state, its **G9**
// provenance, `submissionOpen`, the outstanding count, the caller's member kind, colony id and
// sandbox scope **all ride the phase read** (`GET /rounds/tournament` answers one DTO). So this
// is one channel carrying one DTO, and only the `gradedRounds`/`members` payload needs a second
// request. That is why the same snapshot feeds the phase chrome, the waiting states, the **G9**
// notice, the selection affordance's enablement and the mutation gate.
//
// **What this module refuses to do**, each for a specified reason:
//
//   * it **enumerates no phase machine of its own**. The reported phase is carried verbatim and
//     *classified* into a surface with an explicit unknown arm, so a phase the owning change adds
//     or renames degrades to an inert waiting state instead of being mapped onto the wrong one;
//   * it **never advances the phase locally**. A local zero is presentation: the rendered phase
//     is unchanged, no notice is raised, and — the part that is easy to get wrong — the zero
//     issues **no request of its own**. The ordinary cadence, already short by then, is what
//     observes the server's transition;
//   * it **derives nothing tick-stamped**. The remainder is a countdown on a screen; the server's
//     deadline stays the authority.

import { useMemo, useSyncExternalStore } from 'react';
import { performanceClock, type Clock } from '../../canvas/frame-scheduler';
import type { Scheduler } from '../../net/wire/connection';
import type {
  RoundsTournamentTransport,
  SubmissionReadDto,
  TournamentReadDto,
} from '../../net/rounds-http';

/**
 * The surface class a reported phase falls into.
 *
 * Deliberately **not** the server's vocabulary re-typed: `unknown` is a first-class arm covering
 * both a phase this build has no surface for and a boot before the first reading, and it is what
 * makes "a client that hard-coded one list would be wrong the moment the owner added a phase"
 * false of this one.
 */
export type PhaseSurface =
  | 'unknown'
  | 'gathering'
  | 'editing'
  | 'simulating'
  | 'reviewing'
  | 'standings'
  | 'cancelling'
  | 'cancelled';

export interface RoundsPhaseSnapshot {
  /** Whether any server reading has landed. `false` renders **no** phase chrome — not a
   * placeholder countdown, not a zero, not an invented phase name. */
  known: boolean;
  surface: PhaseSurface;
  /** The reported phase **verbatim**, so copy can name what the server said even when this build
   * has no surface for it. `null` before the first reading. */
  reportedPhase: string | null;
  round: number | null;
  roundsTotal: number | null;
  /**
   * Seconds left in the phase, counted down locally between readings.
   *
   * `null` means **no player-facing deadline** — the simulating phase, whose end is reported —
   * and the chrome renders its placeholder for it rather than a zero or an invented duration.
   */
  remainingSeconds: number | null;
  /** Whether the remainder is inside the final interval. A change of emphasis, never a failure. */
  urgent: boolean;
  /** Whether the locally counted remainder has reached zero. Presentation only. */
  locallyExpired: boolean;
  /**
   * Whether the client may offer a mutation at all. `false` for every phase but editing, for a
   * spectator, and once the local countdown has expired — the presentational half of the
   * two-input gate (**D6**), never an assertion that the server has sealed anything.
   */
  mutationAllowed: boolean;
  submissionOpen: boolean;
  submission: SubmissionReadDto | null;
  outstanding: number | null;
  memberKind: string | null;
  isSpectator: boolean;
  colonyId: number | null;
  /** The caller's live sandbox scope for the round. `null` for a spectator. */
  sandbox: string | null;
  displayName: string | null;
  tournamentId: string | null;
  /** The caller's own join ordinal — 0 is the host's. `null` before the first reading. Carried so
   * the join-window waiting state can confirm the player's own membership
   * (`client-rounds-phase`). */
  joinOrdinal: number | null;
  /** How many members have joined so far. Present (non-null) only in the `gathering` surface —
   * every other phase has nobody left to count. */
  joined: number | null;
}

export interface RoundsPhaseChannel {
  /** The cached snapshot. Identical between notifications, by reference. */
  snapshot(): RoundsPhaseSnapshot;
  subscribe(onChange: () => void): () => void;
  /**
   * Starts the poll loop; the returned function stops **that** run.
   *
   * Calling it again retires the previous run and starts a fresh one, so a `StrictMode`
   * setup → cleanup → setup cycle leaves exactly one loop polling — the identity-token
   * discipline `createRoundsPromptChannel` already uses, and for the same reason.
   */
  start(): () => void;
  /**
   * Reads once, now, outside the cadence.
   *
   * Bound to the run that was active when it was called: if that run is stopped or replaced while
   * the request is in flight, the answer is discarded rather than reported. A channel that was
   * never started has no run to lose, so its refresh always applies.
   */
  refresh(): Promise<void>;
}

export interface RoundsPhaseChannelDeps {
  transport: Pick<RoundsTournamentTransport, 'fetchTournament'>;
  clock?: Clock;
  scheduler?: Scheduler;
  /** Called with each fresh reading, before the snapshot is published. The composition root's
   * hook for the round-boundary rebind (**D7**) and the standings re-read — kept here rather
   * than making the root poll a second time for the same DTO. */
  onReading?: (reading: TournamentReadDto) => void;
}

// --- the cadence ladder (**D1**) --------------------------------------------------------
//
// Derived from the remainder, and living in **one** module so the numbers cannot scatter.

/** The interval while the remainder is comfortable. */
export const PHASE_POLL_COMFORTABLE_MS = 10_000;

/**
 * The interval inside the final interval — and while the phase carries no deadline at all.
 *
 * The no-deadline case is the simulating phase, whose end is *reported* rather than counted
 * down: the ordinary phase read leaving that state is precisely how a client learns the round
 * landed, because in a graded round the client holds no run id for the round's batch job. That
 * is the completion signal the spec permits, and it is emphatically not a progress poll — there
 * is no per-tick or per-frame request anywhere in this module.
 */
export const PHASE_POLL_SHORT_MS = 3_000;

/** The final interval, in seconds: the urgent presentation's window, and the short cadence's. */
export const PHASE_URGENT_THRESHOLD_SECONDS = 30;

/** How often the countdown re-derives its whole-second value. Four times a second, so the
 * displayed second is never more than a quarter-second stale (`rounds-prompt.ts`'s precedent). */
const COUNTDOWN_TICK_MS = 250;

/** Classifies a reported phase. The unknown arm is explicit and load-bearing. */
function surfaceOf(phase: string): PhaseSurface {
  switch (phase) {
    case 'gathering':
      return 'gathering';
    case 'sandbox':
      return 'editing';
    case 'simulate':
      return 'simulating';
    case 'review':
      return 'reviewing';
    case 'standings':
      return 'standings';
    case 'cancelling':
      return 'cancelling';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
}

/** The poll interval for a remainder. A `null` remainder is the no-deadline phase. */
export function phasePollIntervalMs(remainingSeconds: number | null): number {
  if (remainingSeconds === null) {
    return PHASE_POLL_SHORT_MS;
  }
  return remainingSeconds <= PHASE_URGENT_THRESHOLD_SECONDS
    ? PHASE_POLL_SHORT_MS
    : PHASE_POLL_COMFORTABLE_MS;
}

/**
 * Whether the chrome renders its urgent presentation.
 *
 * **Monotone in the remainder** by construction — it is a single comparison against a constant,
 * so a countdown crosses it exactly once and cannot flicker — and unreachable outside the
 * interval, including for a phase with no deadline at all.
 */
export function isUrgentRemainder(remainingSeconds: number | null): boolean {
  return remainingSeconds !== null && remainingSeconds <= PHASE_URGENT_THRESHOLD_SECONDS;
}

const UNKNOWN_SNAPSHOT: RoundsPhaseSnapshot = {
  known: false,
  surface: 'unknown',
  reportedPhase: null,
  round: null,
  roundsTotal: null,
  remainingSeconds: null,
  urgent: false,
  locallyExpired: false,
  mutationAllowed: false,
  submissionOpen: false,
  submission: null,
  outstanding: null,
  memberKind: null,
  isSpectator: false,
  colonyId: null,
  sandbox: null,
  displayName: null,
  tournamentId: null,
  joinOrdinal: null,
  joined: null,
};

export function createRoundsPhaseChannel(deps: RoundsPhaseChannelDeps): RoundsPhaseChannel {
  const { transport } = deps;
  const clock = deps.clock ?? performanceClock;
  const scheduler = deps.scheduler ?? wallClockScheduler();

  let reading: TournamentReadDto | null = null;
  /** The absolute instant the phase expires, in the injected clock's frame. `null` for a phase
   * with no deadline — never a zero standing in for "we do not know". */
  let deadlineMs: number | null = null;
  /** The cached whole-second remainder. Never recomputed inside `snapshot()`. */
  let remaining: number | null = null;
  let cached: RoundsPhaseSnapshot = UNKNOWN_SNAPSHOT;
  let countdownTimer: number | null = null;
  let pollTimer: number | null = null;
  let activePoll: object | null = null;
  const listeners = new Set<() => void>();

  const notify = (): void => {
    for (const listener of listeners) {
      listener();
    }
  };

  /** Rebuilds the cached snapshot and notifies **only** if something a consumer can see moved. */
  const publish = (): void => {
    const next = build();
    if (equal(next, cached)) {
      return;
    }
    cached = next;
    notify();
  };

  function build(): RoundsPhaseSnapshot {
    if (reading === null) {
      return UNKNOWN_SNAPSHOT;
    }
    const surface = surfaceOf(reading.phase);
    const isSpectator = reading.memberKind === 'spectator';
    const locallyExpired = remaining !== null && remaining <= 0;
    return {
      known: true,
      surface,
      reportedPhase: reading.phase,
      round: reading.currentRound,
      roundsTotal: reading.roundsTotal,
      remainingSeconds: remaining,
      urgent: isUrgentRemainder(remaining),
      locallyExpired,
      // Editing only, competitor only, and not once the local countdown has run out. The seal
      // itself is a server refusal (**D6**); this flag is the presentational half.
      mutationAllowed: surface === 'editing' && !isSpectator && !locallyExpired,
      submissionOpen: reading.submissionOpen,
      submission: reading.submission,
      outstanding: reading.outstanding,
      memberKind: reading.memberKind,
      isSpectator,
      colonyId: reading.colonyId,
      sandbox: reading.sandbox,
      displayName: reading.displayName,
      tournamentId: reading.tournamentId,
      joinOrdinal: reading.joinOrdinal,
      joined: reading.joined,
    };
  }

  const secondsLeft = (): number | null =>
    deadlineMs === null ? null : Math.max(0, Math.ceil((deadlineMs - clock.now()) / 1000));

  const stopCountdown = (): void => {
    if (countdownTimer !== null) {
      scheduler.clearTimer(countdownTimer);
      countdownTimer = null;
    }
  };

  const tickCountdown = (): void => {
    countdownTimer = null;
    remaining = secondsLeft();
    publish();
    // The countdown stops at zero. It raises nothing and sends nothing: reaching zero is a
    // presentational event, and the poll loop below is a separate timer that keeps observing
    // the server's own transition at the already-short cadence.
    if (remaining !== null && remaining > 0) {
      countdownTimer = scheduler.setTimer(tickCountdown, COUNTDOWN_TICK_MS);
    }
  };

  /** Adopts a server reading's remainder, replacing any local extrapolation outright. */
  const adoptRemainder = (remainingS: number | null): void => {
    if (remainingS === null) {
      stopCountdown();
      deadlineMs = null;
      remaining = null;
      return;
    }
    deadlineMs = clock.now() + remainingS * 1000;
    // `deadlineMs` is non-null on this branch, so the remainder is a number — bound locally so
    // that is a fact the compiler can see rather than one the reader has to reconstruct.
    const seconds = secondsLeft() ?? 0;
    remaining = seconds;
    if (seconds > 0) {
      if (countdownTimer === null) {
        countdownTimer = scheduler.setTimer(tickCountdown, COUNTDOWN_TICK_MS);
      }
    } else {
      stopCountdown();
    }
  };

  const apply = (next: TournamentReadDto | null): void => {
    if (next === null) {
      // An unreadable surface is **not** a cancelled tournament, not a phase change, and not a
      // zero: a cancellation is adopted from the server's own report or not at all. The last
      // known reading stands, and the countdown keeps running against the last stated deadline.
      return;
    }
    reading = next;
    adoptRemainder(next.remainingS);
    deps.onReading?.(next);
    publish();
  };

  /**
   * Reads once, and applies the answer **only if the run that asked still owns the channel**
   * (implementation review **F1**).
   *
   * The lifecycle check has to sit *here*, before `apply`, and not merely after the await in
   * {@link poll}: `apply` raises `onReading`, and `onReading` is the composition root's
   * round-boundary detector. A response belonging to a stopped run that reached `onReading` would
   * report *its* round as the current one — so a round-1 answer landing after the replacement
   * channel had already bound round 2 read as a fresh boundary and rebound the client, the store
   * and the run history back onto the previous round. Checking after `apply` is too late by
   * exactly the call that does the damage.
   *
   * The token is `activePoll` **as it stood when the request was issued**, which also gives
   * `refresh()` the right semantics on both sides: a channel that was never started reads `null`
   * on entry and `null` afterwards, so an unstarted channel still applies its answer, while a
   * refresh issued under a run that has since been stopped (or replaced) applies nothing.
   */
  const readOnce = async (): Promise<void> => {
    const token = activePoll;
    const next = await transport.fetchTournament();
    if (activePoll !== token) {
      return;
    }
    apply(next);
  };

  const scheduleNext = (token: object): void => {
    pollTimer = scheduler.setTimer(() => {
      pollTimer = null;
      void poll(token);
    }, phasePollIntervalMs(remaining));
  };

  const poll = async (token: object): Promise<void> => {
    if (activePoll !== token) {
      return;
    }
    await readOnce();
    if (activePoll !== token) {
      return;
    }
    scheduleNext(token);
  };

  return {
    snapshot: () => cached,
    subscribe: (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    refresh: readOnce,
    start: () => {
      const token = {};
      activePoll = token;
      void poll(token);
      return () => {
        if (activePoll === token) {
          activePoll = null;
        }
        if (pollTimer !== null) {
          scheduler.clearTimer(pollTimer);
          pollTimer = null;
        }
        stopCountdown();
      };
    },
  };
}

/**
 * The read-only face a screen consumes. Narrower than the channel on purpose: a screen may read
 * and subscribe, and has no business starting, stopping or refreshing the poll loop — that is
 * the composition root's, which is the **fourth** wall-clock owner it holds.
 */
export type RoundsPhaseSource = Pick<RoundsPhaseChannel, 'snapshot' | 'subscribe'>;

const NO_SUBSCRIBE = (): (() => void) => () => {};
const NO_PHASE = (): RoundsPhaseSnapshot => UNKNOWN_SNAPSHOT;

/**
 * Reads a {@link RoundsPhaseSource} as a re-rendering value (`useAdvisoryPromptCooldown`'s
 * precedent).
 *
 * With **no source injected** — a live deployment, or a rounds boot before the first phase read
 * — this returns the unknown snapshot, whose `known` is `false`. That is what lets a screen
 * render **no phase chrome at all** rather than a placeholder countdown or a zero: the caller
 * branches on `known`, and there is no value here that could be mistaken for a real deadline.
 */
export function useRoundsPhase(source?: RoundsPhaseSource): RoundsPhaseSnapshot {
  const subscribe = useMemo(
    () =>
      source === undefined ? NO_SUBSCRIBE : (onChange: () => void) => source.subscribe(onChange),
    [source],
  );
  const snapshot = useMemo(
    () => (source === undefined ? NO_PHASE : () => source.snapshot()),
    [source],
  );
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** Field-wise comparison, so a re-read that changed nothing does not notify every consumer. */
function equal(a: RoundsPhaseSnapshot, b: RoundsPhaseSnapshot): boolean {
  return (
    a.known === b.known &&
    a.surface === b.surface &&
    a.reportedPhase === b.reportedPhase &&
    a.round === b.round &&
    a.roundsTotal === b.roundsTotal &&
    a.remainingSeconds === b.remainingSeconds &&
    a.urgent === b.urgent &&
    a.locallyExpired === b.locallyExpired &&
    a.mutationAllowed === b.mutationAllowed &&
    a.submissionOpen === b.submissionOpen &&
    a.outstanding === b.outstanding &&
    a.memberKind === b.memberKind &&
    a.isSpectator === b.isSpectator &&
    a.colonyId === b.colonyId &&
    a.sandbox === b.sandbox &&
    a.displayName === b.displayName &&
    a.tournamentId === b.tournamentId &&
    a.joinOrdinal === b.joinOrdinal &&
    a.joined === b.joined &&
    a.submission?.provenance === b.submission?.provenance &&
    a.submission?.candidateRef === b.submission?.candidateRef
  );
}

/** The production timer seam (`bootstrap.tsx`'s `wallClockScheduler`, kept local so this module
 * imports no React tree — `rounds-prompt.ts`'s precedent). */
function wallClockScheduler(): Scheduler {
  return {
    setTimer: (fn, ms) => setTimeout(fn, ms) as unknown as number,
    clearTimer: (id) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>),
  };
}
